import { useEffect, useState } from 'react'
import { X } from 'lucide-react'

interface InstallPrompt extends Event {
    prompt(): Promise<void>
    userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

/** Install is offered only when the browser reports that this app is eligible. */
export function PwaInstall({ name }: { name: string }) {
    const [prompt, setPrompt] = useState<InstallPrompt | null>(null)
    const [installing, setInstalling] = useState(false)
    const dismissalKey = `pwa-install-dismissed:${name}`
    const [dismissed, setDismissed] = useState(() => {
        try {
            return sessionStorage.getItem(dismissalKey) === 'true'
        } catch {
            return false
        }
    })
    useEffect(() => {
        const available = (event: Event) => {
            event.preventDefault()
            setPrompt(event as InstallPrompt)
        }
        const installed = () => setPrompt(null)
        window.addEventListener('beforeinstallprompt', available)
        window.addEventListener('appinstalled', installed)
        if ('serviceWorker' in navigator && window.isSecureContext) {
            void navigator.serviceWorker.register('/sw.js').catch(error => console.warn('PWA registration failed', error))
        }
        return () => {
            window.removeEventListener('beforeinstallprompt', available)
            window.removeEventListener('appinstalled', installed)
        }
    }, [])
    if (!prompt || dismissed) return null
    return <div className="fixed bottom-4 right-4 z-40 flex max-w-[calc(100vw-2rem)] items-center rounded-lg bg-blue-600 text-white shadow-lg">
        <button type="button" disabled={installing}
        className="min-h-11 rounded-l-lg px-4 py-2 text-sm font-medium hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-50"
        onClick={async () => {
            setInstalling(true)
            try {
                await prompt.prompt()
                await prompt.userChoice
            } catch (error) {
                console.warn('PWA install prompt failed', error)
            } finally {
                setPrompt(null)
                setInstalling(false)
            }
        }}>Install {name}</button>
        <button type="button" aria-label={`Dismiss install ${name} prompt`} title="Dismiss install prompt"
            className="flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-r-lg hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            onClick={() => {
                setDismissed(true)
                setPrompt(null)
                try {
                    sessionStorage.setItem(dismissalKey, 'true')
                } catch {
                    // Keep dismissal effective in memory when browser storage is unavailable.
                }
            }}><X size={18} aria-hidden="true" /></button>
    </div>
}
